import { useMsal } from '@azure/msal-react';
import { makeStyles, mergeClasses, Tab, TabList, Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useStores, useTitle } from '../../common/hooks';
import { DocumentGroupTypeCode, ProgramDocument } from '../../models';
import { parseAsString, useQuery } from '../../shared/hooks';
import { CustomMessageBar, getCardThemeTypeByIndex, PageTitle, ProgramDocumentCard } from '../shared-components';
import { useAppToast } from '../../shared/toast';
import { FlexBox } from '../shared-components/flex-box';
import Loading from '../shared-components/Loading';
import { Notifications } from '../shared-components/Notifications';
import './AwardResourcesPage.scss';

interface AwardResourcesPageProps {
  isShowSubmitQuestionnaireSuccessToast: boolean;
}

const useTabListStyle = makeStyles({
  root: {
    alignItems: 'center',
  },
});

const AwardResourcesPageImpl: React.FC<AwardResourcesPageProps> = (props) => {
  const {
    isShowSubmitQuestionnaireSuccessToast,
  } = props;

  const {
    instance
  } = useMsal();

  const {
    getQueryParam,
    updateQuery,
  } = useQuery();

  const {
    apiStore,
    awardStore,
    userStore,
    notificationStore,
    awardResourcesPageUiStore,
  } = useStores();

  const styles = useTabListStyle();

  const isWinner = userStore.userInfo?.isWinner ?? false;
  useTitle('Award Resources');

  const currentDocumentGroup = getQueryParam<DocumentGroupTypeCode>('tab', parseAsString<DocumentGroupTypeCode>);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { showToast } = useAppToast();

  const getNotification = async (): Promise<void> => {
    try {
      const programRef = await apiStore.getDefaultProgramRef(instance);
      awardStore.setDefaultProgramRef(programRef);
      const programNotifications = await apiStore.getProgramNotifications(instance, programRef.programId, 'winner-resource');
      notificationStore.setNotifications('winner-resource', programNotifications);
    } catch (error) {
      // TODO - handle error
    }
  }

  const getAwardResources = async (): Promise<void> => {
    try {
      const awardProgramDocuments = await apiStore.getProgramDocuments(instance, {
        type: 'award-resource'
      });
      awardResourcesPageUiStore.setProgramDocuments('award-resource', awardProgramDocuments);
    } catch (error) {
      // TODO: Handle error
    }
  };

  const getWinnerResources = async (): Promise<void> => {
    try {
      const winnerProgramDocuments = await apiStore.getProgramDocuments(instance, {
        type: 'winner-resource'
      });
      awardResourcesPageUiStore.setProgramDocuments('winner-resource', winnerProgramDocuments);

      await getNotification();
    } catch (error) {
      // TODO: Handle error
    }
  };

  const setCurrentDocumentGroup = (tab: DocumentGroupTypeCode) => {
    updateQuery(
      { tab },
      { replace: true }
    );
  };

  const generateDefaultTabValueFromQuery = () => {
    if (!isWinner) return 'award-resource';
    if (!currentDocumentGroup) return 'winner-resource';
    return currentDocumentGroup;
  };

  const applyFilters = () => {
    updateQuery(
      {
        tab: generateDefaultTabValueFromQuery()
      } satisfies { tab: DocumentGroupTypeCode },
      { replace: true }
    );
  };

  const preloadData = async () => {
    if (currentDocumentGroup == 'judging-resource') return;

    setIsLoading(true);

    try {
      await Promise.allSettled([
        getAwardResources(),
        isWinner && currentDocumentGroup === 'winner-resource' ? getWinnerResources() : Promise.resolve(),
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const renderProgramDocuments = (programDocuments: ProgramDocument[]) => {
    return (
      <FlexBox
        wrap
        as='section'
        content={programDocuments.length ? 'start' : 'center'}
        className='award-resources-page__tabs-section'
      >
        {
          !!programDocuments.length ? programDocuments.map((programDocument, index) => (
            <ProgramDocumentCard
              key={programDocument.programDocumentId}
              programDocument={programDocument}
              cardTheme={getCardThemeTypeByIndex(index)}
            />
          )) : <Text>No resource found</Text>
        }
      </FlexBox>
    );
  };

  useEffect(() => {
    preloadData();
  }, [currentDocumentGroup]);

  useEffect(() => {
    applyFilters();
    return () => {
      awardResourcesPageUiStore.resetStates();
    }
  }, []);

  useEffect(() => {
    if (!isShowSubmitQuestionnaireSuccessToast) {
      return;
    }

    const timeoutId = setTimeout(() => {
      // Adding a timeout to prevent screen reader conflict with the initial landmark call(first page render)
      showToast({
        title: 'Your award has been accepted. Your marketing and award benefits are available to you below.',
        intent: 'success',
      });
    }, 150);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isShowSubmitQuestionnaireSuccessToast]);

  return (
    <main
      className='award-resources-page'
    >
      {isLoading && <Loading />}
      <PageTitle
        title={currentDocumentGroup === 'winner-resource' && userStore.userInfo?.isWinner ? 'Winner resources' : 'Award resources'}
        subtitle='Microsoft AI Cloud Partner Program'
      />
      {
        currentDocumentGroup === 'winner-resource' && isWinner && notificationStore.dashboardPageNotifications('winner-resource').length > 0 &&
        <Notifications
          notifications={notificationStore.dashboardPageNotifications('winner-resource')}
        />
      }
      {
        currentDocumentGroup === 'winner-resource' && isWinner &&
        <>
          <Text
            as='p'
            block
          >
            Your marketing and award benefits are available to you to begin prepping.
          </Text>
          <Text
            as='p'
            block
          >
            Please do not publish any marketing or award benefits until winners and finalists have been announced officially by Microsoft.
          </Text>
        </>
      }
      <TabList
        className={mergeClasses(styles.root, 'award-resources-page__tabs')}
        selectedValue={currentDocumentGroup ?? 'award-resource'}
        onTabSelect={(_, tabData) => setCurrentDocumentGroup(tabData.value as DocumentGroupTypeCode ?? currentDocumentGroup)}
      >
        <Tab
          value='award-resource'
        >
          Award resources
        </Tab>
        {
          isWinner && (
            <>
              <Text weight='bold'>|</Text>
              <Tab
                value='winner-resource'
              >
                Winner resources
              </Tab>
            </>
          )
        }
      </TabList>
      {
        currentDocumentGroup === 'award-resource' && (
          renderProgramDocuments(awardResourcesPageUiStore.getProgramDocuments('award-resource'))
        )
      }
      {
        currentDocumentGroup === 'winner-resource' && isWinner && (
          renderProgramDocuments(awardResourcesPageUiStore.getProgramDocuments('winner-resource'))
        )
      }
    </main>
  );
};

export const AwardResourcesPage = observer(AwardResourcesPageImpl);
